import { authClient, type Member } from '@/lib/auth-client';

export const getFullOrganization = async () => {
  const { error, data } = await authClient.organization.getFullOrganization();

  if (error) {
    throw new Error(error.message);
  }

  return data;
};

export const listOrganizations = async () => {
  const { error, data } = await authClient.organization.list();

  if (error) {
    throw new Error(error.message);
  }

  return data;
};

export const createAndSetActiveOrganization = async (params: { name: string; slug: string }) => {
  const { error: createError, data: organization } = await authClient.organization.create(params);

  if (createError) {
    throw new Error(createError.message);
  }

  const { error: setActiveError } = await authClient.organization.setActive({
    organizationId: organization?.id,
  });

  if (setActiveError) {
    throw new Error(setActiveError.message);
  }

  return organization;
};

export const setActiveOrganization = async (params: {
  organizationId?: string;
  organizationSlug?: string;
}) => {
  const { error, data } = await authClient.organization.setActive(params);

  if (error) {
    throw new Error(error.message);
  }

  return data;
};

export const inviteMember = async (params: {
  email: string;
  role: 'member' | 'admin' | 'owner';
}) => {
  const { error, data } = await authClient.organization.inviteMember(params);

  if (error) {
    throw new Error(error.message);
  }

  return data;
};

export const cancelInvitation = async (params: { invitationId: string }) => {
  const { error, data } = await authClient.organization.cancelInvitation(params);

  if (error) {
    throw new Error(error.message);
  }

  return data;
};

export const removeMember = async (params: { memberIdOrEmail: string; organizationId: string }) => {
  const { error, data } = await authClient.organization.removeMember(params);

  if (error) {
    throw new Error(error.message);
  }

  return data;
};

export const updateMemberRole = async (params: {
  memberId: string;
  role: 'member' | 'admin' | 'owner';
}) => {
  const { error, data } = await authClient.organization.updateMemberRole(params);

  if (error) {
    throw new Error(error.message);
  }

  return data;
};

export const checkSlug = async (slug: string) => {
  const { error, data } = await authClient.organization.checkSlug({ slug });

  if (error) {
    if (error.code === 'SLUG_IS_TAKEN') {
      return { status: false };
    }

    throw new Error(error.message);
  }

  return data;
};

export const getInvitation = async (params: { invitationId: string }) => {
  const { error, data } = await authClient.organization.getInvitation({
    query: {
      id: params.invitationId,
    },
  });

  if (error) {
    throw new Error(error.message);
  }

  return data;
};

export const rejectInvitation = async (params: { invitationId: string }) => {
  const { error, data } = await authClient.organization.rejectInvitation({
    invitationId: params.invitationId,
  });

  if (error) {
    throw new Error(error.message);
  }

  return data;
};

export const getActiveMember = async () => {
  const { error, data } = await authClient.organization.getActiveMember();

  if (error) {
    throw new Error(error.message);
  }

  return data as Member;
};

export const acceptInvitation = async (params: { invitationId: string }) => {
  const { error } = await authClient.organization.acceptInvitation(params);

  if (error) {
    throw new Error(error.message);
  }

  const { data: newSession, error: newSessionError } = await authClient.getSession({
    query: { disableCookieCache: true },
  });

  if (newSessionError) {
    throw new Error(newSessionError.message);
  }

  return newSession;
};
